import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Folder, FolderPlus, Pencil, Plus, Tags, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SectionCard } from './shared/SectionCard';
import { AgrupadorEditorModal } from './agrupadores/AgrupadorEditorModal';
import { AsociarNombresTable } from './agrupadores/AsociarNombresTable';
import { useAgrupadores } from '../hooks/useAgrupadores';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { SECTION_LABEL_CLASS } from '../constants/ui';
import { CONTABILIDAD_ROUTES } from '../constants/routes';
import { aplanarAgrupadores } from '../utils/agrupadores';
import type { Agrupador, GuardarAgrupadorPayload } from '../types';

type Editor = { agrupador: Agrupador | null; parentId: number | null } | null;

export function AgrupadoresPage() {
  const {
    agrupadores, nombres, isLoading,
    createAgrupador, updateAgrupador, deleteAgrupador, asignarNombres,
    isMutating, isAsignando,
  } = useAgrupadores();
  const { successAlert, errorAlert, questionAlert } = useAlert();
  const { canEditContabilidad } = useAuthorization();
  // Se llega desde egresos o desde cobros pendientes; la flecha vuelve a la de origen.
  const desdeCobros = (useLocation().state as { desde?: string } | null)?.desde === 'cobros';

  const [editor, setEditor] = useState<Editor>(null);

  const porId = useMemo(() => new Map(agrupadores.map((a) => [a.id, a])), [agrupadores]);
  const filas = useMemo(() => aplanarAgrupadores(agrupadores), [agrupadores]);
  const tieneHijos = useMemo(() => new Set(agrupadores.map((a) => a.parentId)), [agrupadores]);

  async function handleSubmit(payload: GuardarAgrupadorPayload) {
    try {
      if (editor?.agrupador) {
        await updateAgrupador({ id: editor.agrupador.id, payload });
      } else {
        await createAgrupador(payload);
      }
      setEditor(null);
    } catch (err) {
      errorAlert({ title: 'Error al guardar el agrupador', exception: err });
    }
  }

  async function handleDelete(agrupador: Agrupador) {
    const { isConfirmed } = await questionAlert({
      title: `¿Eliminar "${agrupador.nombre}"?`,
      text: 'Los nombres asociados quedan sin agrupar. No cambia ningún pago ya registrado.',
      confirmButtonText: 'Sí, eliminar',
    });
    if (!isConfirmed) return;

    try {
      await deleteAgrupador(agrupador.id);
      successAlert({ title: 'Agrupador eliminado', message: `"${agrupador.nombre}" fue eliminado.` });
    } catch (err) {
      errorAlert({ title: 'Error al eliminar', exception: err });
    }
  }

  async function handleAsignar(nombresElegidos: string[], agrupadorId: number | null) {
    try {
      await asignarNombres({ nombres: nombresElegidos, agrupadorId });
    } catch (err) {
      errorAlert({ title: 'Error al asociar', exception: err });
    }
  }

  if (isLoading) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  return (
    <>
      <div className="space-y-5 p-2 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="icon" className="rounded-full" aria-label={`Volver a ${desdeCobros ? 'cobros' : 'egresos'} pendientes`}>
              <Link to={desdeCobros ? CONTABILIDAD_ROUTES.COBROS_PENDIENTES : CONTABILIDAD_ROUTES.EGRESOS_PENDIENTES}><ArrowLeft className="size-4" /></Link>
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Agrupadores</h1>
          </div>
          {canEditContabilidad && (
            <Button size="sm" className="gap-1.5 rounded-full" onClick={() => setEditor({ agrupador: null, parentId: null })}>
              <Plus className="size-4" /> Nuevo agrupador
            </Button>
          )}
        </div>

        <SectionCard className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Folder className="size-4 text-muted-foreground" />
            <p className={SECTION_LABEL_CLASS}>Árbol de agrupadores</p>
          </div>

          {filas.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Todavía no hay agrupadores. Creá uno (por ejemplo, Sellos) y asociale los nombres de ítems.
            </p>
          ) : (
            <ul>
              {filas.map((fila) => {
                const agrupador = porId.get(fila.id)!;
                return (
                  <li
                    key={fila.id}
                    className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-2.5 pr-4 last:border-b-0"
                    style={{ paddingLeft: `${20 + fila.profundidad * 24}px` }}
                  >
                    <Folder className="size-4 shrink-0 text-slate-500" />
                    <span className="text-sm font-medium text-gray-900">{agrupador.nombre}</span>
                    {agrupador.cantidadNombres > 0 && (
                      <Badge variant="outline" className="text-[11px] font-normal">
                        {agrupador.cantidadNombres} {agrupador.cantidadNombres === 1 ? 'nombre' : 'nombres'}
                      </Badge>
                    )}
                    {canEditContabilidad && (
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost" size="icon" className="size-8"
                          aria-label={`Agregar subagrupador a ${agrupador.nombre}`}
                          onClick={() => setEditor({ agrupador: null, parentId: agrupador.id })}
                        >
                          <FolderPlus className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="size-8"
                          aria-label={`Editar ${agrupador.nombre}`}
                          onClick={() => setEditor({ agrupador, parentId: agrupador.parentId })}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost" size="icon" className="size-8 text-rose-600 hover:text-rose-700"
                          aria-label={`Eliminar ${agrupador.nombre}`}
                          disabled={tieneHijos.has(agrupador.id) || isMutating}
                          title={tieneHijos.has(agrupador.id) ? 'Mové o borrá primero sus subagrupadores' : undefined}
                          onClick={() => handleDelete(agrupador)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </SectionCard>

        <SectionCard className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Tags className="size-4 text-muted-foreground" />
            <p className={SECTION_LABEL_CLASS}>Asociar ítems de presupuesto</p>
          </div>
          <AsociarNombresTable
            nombres={nombres}
            agrupadores={agrupadores}
            canEdit={canEditContabilidad}
            isAsignando={isAsignando}
            onAsignar={handleAsignar}
          />
        </SectionCard>
      </div>

      {editor && (
        <AgrupadorEditorModal
          open={true}
          agrupadores={agrupadores}
          agrupador={editor.agrupador}
          parentIdInicial={editor.parentId}
          isSubmitting={isMutating}
          onSubmit={handleSubmit}
          onClose={() => setEditor(null)}
        />
      )}
    </>
  );
}
